(function () {
  const REGISTRY = new WeakMap();

  // --- Helpers (native + custom checkbox patterns) ---
  const isCheckboxEl = (el) =>
    !!el && (el.matches('input[type="checkbox"]') || el.matches(".cbx__input"));
  const isInsideCustomLabel = (el) =>
    !!el && !!el.closest('label.cbx, label[class*="cbx"]');
  const findCheckbox = (scope) =>
    scope.querySelector('input[type="checkbox"], .cbx__input');
  const extractLabelText = (scope) => {
    const custom = scope.querySelector(".cbx__label");
    if (custom) return custom.textContent.trim();
    const span = scope.querySelector("span:not(.cbx__box):not(.cbx__label)");
    if (span) return span.textContent.trim();
    return scope.textContent.trim();
  };

  class Dropdown {
    constructor(root) {
      this.root = root;
      this.multi = root.dataset.multi === "true";
      this.button = root.querySelector(".dd__button");
      this.menu = root.querySelector(".dd__menu");
      this.list = root.querySelector(".dd__list");
      this.options = Array.from(root.querySelectorAll(".dd__option"));
      this.placeholderEl = root.querySelector(".dd__placeholder");
      this.valueEl = root.querySelector(".dd__value");
      this.typeahead = "";
      this.typeaheadTimer = null;
      this.activeIndex = -1;

      this.button.addEventListener("click", () => this.toggle());
      this.button.addEventListener("keydown", (e) => this.#onButtonKeydown(e));
      this.list.addEventListener("click", (e) => this.#onListClick(e));
      this.list.addEventListener("change", (e) => this.#onListChange(e)); // checkbox (native/custom)
      this.list.addEventListener("keydown", (e) => this.#onListKeydown(e));
      document.addEventListener("click", (e) => this.#onDocClick(e));
      this.root.addEventListener("blur", () => this.#onBlur(), true);

      this.#reflectSummary();
      this.close(false);
      REGISTRY.set(this.root, this);
    }

    static get(el) {
      return REGISTRY.get(el.closest("[data-dropdown]"));
    }

    open(focusList = true) {
      if (this.isOpen()) return;

      // Ensure exact pixel width = button width (prevents subpixel overflow)
      const btnRect = this.button.getBoundingClientRect();
      this.menu.style.inlineSize = `${Math.round(btnRect.width)}px`;

      // Reset collision classes before measuring
      this.root.classList.remove("dd--align-end", "dd--drop-up");

      // Show (hidden = false) before measuring so rects are accurate
      this.root.setAttribute("aria-expanded", "true");
      this.button.setAttribute("aria-expanded", "true");
      this.menu.hidden = false;

      // ---- Collision detection ----
      const menuRect = this.menu.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Flip horizontally if overflowing to the right (LTR) or to the left (RTL)
      const isRTL =
        getComputedStyle(document.documentElement).direction === "rtl";

      const overflowRight = menuRect.right > vw - 8; // 8px breathing room
      const overflowLeft = menuRect.left < 8;

      if ((!isRTL && overflowRight) || (isRTL && overflowLeft)) {
        this.root.classList.add("dd--align-end");
        // Re-measure after flipping (optional but safer)
        const flippedRect = this.menu.getBoundingClientRect();
        // If still off-screen on the other side, clamp width to viewport
        if (flippedRect.left < 8) {
          const available = vw - 16; // 8px margins
          this.menu.style.inlineSize = `${Math.min(
            available,
            btnRect.width
          )}px`;
        }
      }

      // Drop upward if not enough space below
      const spaceBelow = vh - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const desiredHeight = Math.min(
        parseFloat(getComputedStyle(this.menu).maxHeight || "9999"),
        9999
      );

      if (
        spaceBelow < Math.min(desiredHeight, 240) &&
        spaceAbove > spaceBelow
      ) {
        this.root.classList.add("dd--drop-up");
      }

      // Clamp scroll area height to available space (below or above)
      // (Assumes .dd__menu > .dd__list is the scrolling element)
      const listEl = this.menu.querySelector(".dd__list");
      if (listEl) {
        if (this.root.classList.contains("dd--drop-up")) {
          const maxH = Math.max(120, Math.floor(spaceAbove - 16)); // top padding
          listEl.style.maxHeight = `${maxH}px`;
        } else {
          const maxH = Math.max(120, Math.floor(spaceBelow - 16));
          listEl.style.maxHeight = `${maxH}px`;
        }
        listEl.style.overflow = "auto";
      }

      this.root.dispatchEvent(new CustomEvent("dd:open", { bubbles: true }));

      if (focusList) {
        const sel = this.options.find(
          (o) => o.getAttribute("aria-selected") === "true"
        );
        const focusTo = sel || this.options[0];
        this.#setActiveIndex(this.options.indexOf(focusTo));
        focusTo?.focus();
      }
    }

    close(focusBtn = true) {
      this.root.setAttribute("aria-expanded", "false");
      this.button.setAttribute("aria-expanded", "false");
      this.menu.hidden = true;
      this.root.dispatchEvent(new CustomEvent("dd:close", { bubbles: true }));
      if (focusBtn) this.button.focus({ preventScroll: true });
    }
    toggle() {
      this.isOpen() ? this.close(false) : this.open();
    }
    isOpen() {
      return this.root.getAttribute("aria-expanded") === "true";
    }

    values() {
      return this.options
        .filter(
          (o) =>
            o.dataset.selectall === undefined &&
            o.getAttribute("aria-selected") === "true"
        )
        .map((o) => o.dataset.value);
    }
    labels() {
      return this.options
        .filter(
          (o) =>
            o.dataset.selectall === undefined &&
            o.getAttribute("aria-selected") === "true"
        )
        .map((o) => extractLabelText(o));
    }
    // Allow passing a single string in single-select mode
    set(values = []) {
      const arr = Array.isArray(values) ? values : [values];
      const setVals = new Set(arr.map(String));
      this.options.forEach((o) => {
        if (o.dataset.selectall !== undefined) return;
        const on = setVals.has(String(o.dataset.value));
        this.#toggleOption(o, on, { silent: true });
      });
      this.#syncSelectAll();
      this.#reflectSummary();
      this.#emitChange();
      if (!this.multi) this.close(); // close after programmatic set in single-select
    }
    clear() {
      this.set([]);
    }

    #onButtonKeydown(e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this.open();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggle();
      }
    }

    #onListClick(e) {
      const opt = e.target.closest(".dd__option");
      if (!opt) return;

      // If clicked directly on a checkbox or inside the custom label, let 'change' handle it
      if (isCheckboxEl(e.target) || isInsideCustomLabel(e.target)) return;

      if (opt.dataset.selectall !== undefined) {
        const allOn = !this.#isSelectAllChecked();
        this.options.forEach((o) => {
          if (o.dataset.selectall !== undefined) return;
          this.#toggleOption(o, allOn, { silent: true });
        });
        this.#syncSelectAll();
        this.#reflectSummary();
        this.#emitChange();
      } else {
        // --- Single-select behavior ---
        if (!this.multi) {
          // Clear others, force this one ON, and close
          this.options.forEach((o) => {
            if (o !== opt && o.dataset.selectall === undefined) {
              this.#toggleOption(o, false, { silent: true });
            }
          });
          this.#toggleOption(opt, true);
          this.#reflectSummary();
          this.close(); // close after pick
          return;
        }

        // --- Multi-select behavior (unchanged) ---
        const next = opt.getAttribute("aria-selected") !== "true";
        this.#toggleOption(opt, next);
        this.#syncSelectAll();
        this.#reflectSummary();
      }
    }

    // Respect checkbox (native or custom) changes
    #onListChange(e) {
      if (!isCheckboxEl(e.target)) return;
      const opt = e.target.closest(".dd__option");
      if (!opt) return;

      if (opt.dataset.selectall !== undefined) {
        const allOn = !!e.target.checked;
        this.options.forEach((o) => {
          if (o.dataset.selectall !== undefined) return;
          this.#toggleOption(o, allOn, { silent: true });
        });
        this.#syncSelectAll();
        this.#reflectSummary();
        this.#emitChange();
        return;
      }

      if (!this.multi) {
        // Single-select: force only this one checked and close
        this.options.forEach((o) => {
          if (o !== opt && o.dataset.selectall === undefined) {
            this.#toggleOption(o, false, { silent: true });
          }
        });
        this.#toggleOption(opt, true);
        this.#reflectSummary();
        this.close();
        return;
      }

      // Multi-select default
      this.#toggleOption(opt, !!e.target.checked);
      this.#syncSelectAll();
      this.#reflectSummary();
    }

    #onListKeydown(e) {
      const max = this.options.length - 1;
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          this.close();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          // Simulate click selection logic
          this.#onListClick({ target: this.options[this.activeIndex] });
          break;
        case "ArrowDown":
          e.preventDefault();
          this.#setActiveIndex(Math.min(max, this.activeIndex + 1));
          this.options[this.activeIndex].focus();
          break;
        case "ArrowUp":
          e.preventDefault();
          this.#setActiveIndex(Math.max(0, this.activeIndex - 1));
          this.options[this.activeIndex].focus();
          break;
        case "Home":
          e.preventDefault();
          this.#setActiveIndex(0);
          this.options[0].focus();
          break;
        case "End":
          e.preventDefault();
          this.#setActiveIndex(max);
          this.options[max].focus();
          break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            this.typeahead += e.key.toLowerCase();
            clearTimeout(this.typeaheadTimer);
            this.typeaheadTimer = setTimeout(() => (this.typeahead = ""), 600);
            const i = this.options.findIndex((o) =>
              extractLabelText(o).toLowerCase().startsWith(this.typeahead)
            );
            if (i > -1) {
              this.#setActiveIndex(i);
              this.options[i].focus();
            }
          }
      }
    }

    #onDocClick(e) {
      if (!this.root.contains(e.target)) this.close(false);
    }
    #onBlur() {
      requestAnimationFrame(() => {
        if (!this.root.contains(document.activeElement)) this.close(false);
      });
    }

    #toggleOption(opt, on, { silent = false } = {}) {
      opt.setAttribute("aria-selected", String(!!on));
      const cb = findCheckbox(opt);
      if (cb) {
        cb.checked = !!on;
        cb.indeterminate = false;
      }
      if (!silent) this.#emitChange();
    }

    #emitChange() {
      const detail = { values: this.values(), labels: this.labels() };
      this.root.dispatchEvent(
        new CustomEvent("dd:change", { detail, bubbles: true })
      );
    }

    #isSelectAllChecked() {
      const nonAll = this.options.filter(
        (o) => o.dataset.selectall === undefined
      );
      return (
        nonAll.length > 0 &&
        nonAll.every((o) => o.getAttribute("aria-selected") === "true")
      );
    }
    #syncSelectAll() {
      const selAll = this.options.find(
        (o) => o.dataset.selectall !== undefined
      );
      if (!selAll) return;
      const nonAll = this.options.filter(
        (o) => o.dataset.selectall === undefined
      );
      const selectedCount = nonAll.filter(
        (o) => o.getAttribute("aria-selected") === "true"
      ).length;
      const on = selectedCount === nonAll.length && nonAll.length > 0;
      selAll.setAttribute("aria-selected", String(on));
      const cb = findCheckbox(selAll);
      if (cb) {
        cb.indeterminate = selectedCount > 0 && selectedCount < nonAll.length;
        cb.checked = on;
      }
    }

    #reflectSummary() {
      const labels = this.labels();
      if (!labels.length) {
        this.valueEl.hidden = true;
        this.placeholderEl.hidden = false;
        return;
      }
      const show =
        labels.length <= 2
          ? labels.join(", ")
          : `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
      this.valueEl.textContent = show;
      this.valueEl.hidden = false;
      this.placeholderEl.hidden = true;
    }

    #setActiveIndex(i) {
      this.activeIndex = i;
      this.options.forEach((o, idx) => (o.dataset.active = String(idx === i)));
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    document
      .querySelectorAll("[data-dropdown]")
      .forEach((root) => new Dropdown(root));
  });
  window.Dropdown = Dropdown;
})();
